import React, { useEffect, useState } from 'react';

import { useTheme } from 'styled-components';
import { FaFileExcel, FaUsers } from 'react-icons/fa';
import { FaFilePdf } from 'react-icons/fa';

import { useHistory, useLocation } from 'react-router-dom';

import api from '~/services/api';

import { exportCsv } from '~/helpers/csv';
import { exportPdf } from '~/helpers/pdf';

import {
  Scroll,
  Container,
  Header,
  Area,
  Grid,
  GridLine,
  GridHeadItem,
  GridItem,
  Progress,
  Loading,
  Empty,
  Summary,
} from '~/components';

import { Align, ClearFiltersBtn, LoadArea, Reflection, Thumb } from './styles';

import ClienteType from '~/@types/cliente';
import { IoMdClose } from 'react-icons/io';

const Clientes: React.FC = () => {
  const history = useHistory();
  const { colors } = useTheme();
  
  const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  };

  let query = useQuery();

  const [inLoad, setInLoad] = useState(false);
  const [isPristine, setIsPristine] = useState(true);
  const [busca, setBusca] = useState('');

  const [clientes, setClientes] = useState<ClienteType[]>([]);

  const fetchClientes = async (pStrSearch: string) => {
    try {
      setIsPristine(false);
      setInLoad(true);
      setClientes([]);

      const response = await api.get(`clientes?busca=${pStrSearch}`);

      const { data } = response;


      if (data && Array.isArray(data) && data.length) {
        setClientes(data);
      }

      setInLoad(false);
    } catch (e) {
      setInLoad(false);
    }
  };

  const gerarRelatorioCSV = () => {
    let data: string[] = [];

    data.push('IDCLIENTE,NOME;TELEFONE;EMAIL');

    let c;
    for (c in clientes) {
      const cliente = clientes[c];
      data.push(
        `${cliente.idcliente};${cliente.nome};${cliente.telefone};${cliente.email}`,
      );
    }

    const csv = data.join('\n');

    exportCsv(csv, 'PRODUTOS');
  };

  const gerarRelatorioPDF = () => {
    const cabecalho = ['IDCLIENTE', 'NOME', 'TELEFONE', 'EMAIL'];

    const data: string[][] = [];

    let c;
    for (c in clientes) {
      const cliente = clientes[c];

      const linha: string[] = [];
      linha.push(cliente.idcliente);
      linha.push(cliente.nome);
      linha.push(`${cliente.telefone}`);
      linha.push(cliente.email);
      data.push(linha);
    }

    exportPdf(cabecalho, data, 'PRODUTOS');
  };

  const resetar = () => {
    query.delete('busca');
    history.push({
      pathname: '/clientes',
      search: `?${query.toString()}`,
    });
  };

  const buscarClientes = async (pStrSearch: string) => {
    if (pStrSearch && pStrSearch !== '') {
      query.set('busca', pStrSearch);
    } else {
      query.delete('busca');
    }
    history.push({
      pathname: '/clientes',
      search: `?${query.toString()}`,
    });
  };

  const exibirReflexao = () => {
    if (query.get('busca')) {
      return true;
    }
    return false;
  };

  const verificarReflexao = (pNome: string) => {
    if (query.get(pNome)) {
      return true;
    }
    return false;
  };

  const removerReflexao = (pNome: string) => {
    query.delete(pNome);
    history.push({
      pathname: '/clientes',
      search: `?${query.toString()}`,
    });
  };

  useEffect(() => {
    const sBusca = query.get('busca') || '';
    setBusca(sBusca);
    setIsPristine(true);
    setClientes([]);

    if (sBusca && sBusca !== '') {
      fetchClientes(sBusca);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <>
      {inLoad ? <Progress /> : null}

      <Scroll>
        <Container>
          <Header
            title="Clientes"
            subtitle="Visualize seus clientes de forma rápida"
            isSearch
            onSearch={buscarClientes}
            placeholder="Buscar em clientes..."
            icon={
              <>
                <FaUsers size={32} color={colors.IC1} />
              </>
            }
          />

          {clientes.length ? (
            <Summary>
              <h1>
                {clientes.length > 1
                  ? `${clientes.length} clientes encontrados`
                  : 'Um clientes encontrado'}
              </h1>
              <div>
                <button
                  className="excel"
                  onClick={gerarRelatorioCSV}
                  title="Exportar para Excel"
                >
                  <FaFileExcel size={22} color={colors.IC2} />
                  <div>
                    <h3>EXPORTAR</h3>
                    <h2>EXCEL</h2>
                  </div>
                </button>
                <button
                  className="pdf"
                  onClick={gerarRelatorioPDF}
                  title="Exportar para PDF"
                >
                  <FaFilePdf size={22} color={colors.IC2} />
                  <div>
                    <h3>EXPORTAR</h3>
                    <h2>PDF</h2>
                  </div>
                </button>
              </div>
            </Summary>
          ) : null}

          {exibirReflexao() ? (
            <Reflection>
              <h1>Filtros ativos:</h1>
              <main>
                {verificarReflexao('busca') ? (
                  <div>
                    <button onClick={() => removerReflexao('busca')}>
                      <IoMdClose size={26} color={colors.IC1} />
                    </button>
                    <span>
                      <strong>BUSCA POR:</strong>
                      <small>{busca}</small>
                    </span>
                  </div>
                ) : null}
              </main>
              <ClearFiltersBtn onClick={resetar}>
                LIMPAR TODOS OS FILTROS
              </ClearFiltersBtn>
            </Reflection>
          ) : null}

          {!inLoad ? (
            <Area>
              {clientes.length ? (
                <Grid>
                  <GridLine>
                    <GridHeadItem title="" width={80} />
                    <GridHeadItem title="NOME" />
                    <GridHeadItem title="TELEFONE" width={200} />
                    <GridHeadItem title="E-MAIL" />
                  </GridLine>

                  {clientes.map((cliente, key) => (
                    <GridLine key={cliente.idcliente}>
                      <GridItem width={80}>
                        <Thumb>
                          <span>{`${cliente.nome[0]}`}</span>
                        </Thumb>
                      </GridItem>
                      <GridItem>
                        <Align>
                          <strong>{cliente.nome.toUpperCase()}</strong>
                        </Align>
                      </GridItem>
                      <GridItem width={200}>
                        <Align>{cliente.telefone}</Align>
                      </GridItem>
                      <GridItem>
                        <Align>{cliente.email}</Align>
                      </GridItem>
                    </GridLine>
                  ))}
                </Grid>
              ) : (
                <>
                  {isPristine ? (
                    <Empty>Faça uma busca para encontrar o cliente!</Empty>
                  ) : (
                    <Empty>Nenhum cliente encontrado.</Empty>
                  )}
                </>
              )}
            </Area>
          ) : (
            <LoadArea>
              <Loading color={colors.IC1} size={42} />
            </LoadArea>
          )}
        </Container>
      </Scroll>
    </>
  );
};

export default Clientes;
